import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminCategories = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    parent_id: '',
    is_active: true,
    sort_order: 0
  });

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  // Ouvrir automatiquement le modal d'ajout si on vient de /admin/categories/add
  useEffect(() => {
    if (location.pathname === '/admin/categories/add') {
      if (!loading && categories.length >= 0) {
        setShowAddModal(true);
        navigate('/admin/categories', { replace: true });
      }
    }
  }, [location.pathname, loading, categories.length, navigate]);

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = categories;

    if (filters.search) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(filters.search.toLowerCase()))
      );
    }

    if (filters.status) {
      filtered = filtered.filter(category => category.is_active === (filters.status === 'active'));
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'parent':
            aValue = (a.parent?.name || '').toLowerCase();
            bValue = (b.parent?.name || '').toLowerCase();
            break;
          case 'sort_order':
            aValue = parseInt(a.sort_order) || 0;
            bValue = parseInt(b.sort_order) || 0;
            break;
          case 'status':
            aValue = a.is_active ? 1 : 0;
            bValue = b.is_active ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCategories(filtered);
    setCurrentPage(1);
  }, [categories, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const categoriesData = await adminApi.getCategories();
      const categoriesList = Array.isArray(categoriesData) ? categoriesData : (categoriesData?.data || []);
      setCategories(categoriesList);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      addNotification('Erreur lors du chargement des catégories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: '',
      parent_id: '',
      is_active: true,
      sort_order: 0
    });
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const categoryData = {
        name: formData.name,
        description: formData.description || '',
        icon: formData.icon || null,
        parent_id: formData.parent_id && formData.parent_id !== '' ? parseInt(formData.parent_id) : null,
        is_active: formData.is_active === true || formData.is_active === 'true',
        sort_order: parseInt(formData.sort_order) || 0
      };

      let savedCategory;
      if (editingCategory) {
        const response = await adminApi.updateCategory(editingCategory.id, categoryData);
        // La réponse peut être directement l'objet ou dans response.data
        savedCategory = response?.data || response;

        // Si la réponse contient un objet 'data' avec les propriétés de la catégorie
        if (savedCategory && typeof savedCategory === 'object' && savedCategory.id) {
          // C'est bon
        } else if (response && typeof response === 'object' && response.id) {
          savedCategory = response;
        } else {
          throw new Error('Réponse invalide du serveur');
        }

        setCategories(prev => prev.map(category =>
          category.id === editingCategory.id ? savedCategory : category
        ));
        addNotification('Catégorie mise à jour avec succès', 'success');
      } else {
        const response = await adminApi.createCategory(categoryData);
        // La réponse peut être directement l'objet ou dans response.data
        savedCategory = response?.data || response;

        // Si la réponse contient un objet 'data' avec les propriétés de la catégorie
        if (savedCategory && typeof savedCategory === 'object' && savedCategory.id) {
          // C'est bon
        } else if (response && typeof response === 'object' && response.id) {
          savedCategory = response;
        } else {
          throw new Error('Réponse invalide du serveur');
        }

        setCategories(prev => [...prev, savedCategory]);
        addNotification('Catégorie créée avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      // Recharger les données depuis le serveur pour avoir les relations complètes (parent, etc.)
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || '',
      parent_id: category.parent_id || '',
      is_active: category.is_active !== undefined ? category.is_active : true,
      sort_order: category.sort_order || 0
    });
    setShowAddModal(true);
  };

  const handleViewDetails = (category) => {
    setSelectedCategory(category);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      await adminApi.deleteCategory(selectedCategory.id);
      setCategories(prev => prev.filter(category => category.id !== selectedCategory.id));
      setShowDeleteModal(false);
      setSelectedCategory(null);
      addNotification('Catégorie supprimée avec succès', 'success');
      // Recharger les données pour s'assurer que tout est à jour
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      addNotification(errorMessage, 'error');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const category = categories.find(c => c.id === id);
      const updatedCategory = await adminApi.updateCategory(id, {
        ...category,
        is_active: !category.is_active
      });
      const savedCategory = updatedCategory.data || updatedCategory;
      setCategories(prev => prev.map(c => c.id === id ? savedCategory : c));
      addNotification(`Catégorie ${!category.is_active ? 'activée' : 'désactivée'} avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      addNotification('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  // Fonction pour exporter les catégories en CSV
  const handleExport = () => {
    try {
      const categoriesToExport = filteredCategories.length > 0 ? filteredCategories : categories;

      if (categoriesToExport.length === 0) {
        addNotification('Aucune catégorie à exporter', 'warning');
        return;
      }

      const headers = [
        'ID',
        'Nom',
        'Description',
        'Icône',
        'Catégorie parente',
        'Ordre de tri',
        'Statut',
        'Actif',
        'Date de création'
      ];

      const csvRows = categoriesToExport.map(category => {
        const row = [
          category.id || '',
          `"${(category.name || '').replace(/"/g, '""')}"`,
          `"${(category.description || '').replace(/"/g, '""')}"`,
          category.icon || '',
          category.parent?.name || 'N/A',
          category.sort_order || '0',
          category.is_active ? 'Actif' : 'Inactif',
          category.is_active ? 'Oui' : 'Non',
          category.created_at ? new Date(category.created_at).toLocaleDateString('fr-FR') : ''
        ];
        return row.join(',');
      });

      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const date = new Date().toISOString().split('T')[0];
      const filename = `categories_${date}.csv`;
      link.setAttribute('download', filename);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`${categoriesToExport.length} catégorie(s) exportée(s) avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('Erreur lors de l\'export des catégories', 'error');
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCategories = filteredCategories.slice(startIndex, endIndex);

  // Obtenir les catégories parentes (sans la catégorie en cours d'édition)
  const getParentCategories = () => {
    return categories.filter(cat =>
      !editingCategory || cat.id !== editingCategory.id
    );
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Chargement des catégories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Gestion</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Catégories</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">Gestion des Catégories</h1>
        <p className="page-subtitle">
          Créer et gérer les catégories de produits agricoles
        </p>
      </div>

      {/* Filtres et Actions */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-3 flex-1">
          {/* Recherche */}
          <input
            type="text"
            name="search"
            placeholder="Rechercher une catégorie..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />

          {/* Filtre par statut */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>

          {/* Bouton pour effacer les filtres */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary text-sm"
          >
            Effacer
          </button>
        </div>

        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="btn btn-primary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Ajouter une Catégorie
        </button>
      </div>

      {/* Categories DataTable */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Liste des Catégories
            <span className="text-sm text-gray-500 ml-2">
              ({filteredCategories.length} catégorie{filteredCategories.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="table-actions">
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              disabled={filteredCategories.length === 0}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Nom
                    {sortField === 'name' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('parent')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Catégorie parente
                    {sortField === 'parent' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('sort_order')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Ordre
                    {sortField === 'sort_order' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Statut
                    {sortField === 'status' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCategories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <p className="text-lg font-medium">Aucune catégorie trouvée</p>
                      <p className="text-sm">Commencez par ajouter votre première catégorie</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentCategories.map((category) => (
                  <tr key={category.id}>
                    <td>
                      <div className="flex items-center">
                        {category.icon && (
                          <span className="text-2xl mr-3">{category.icon}</span>
                        )}
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {category.name}
                          </div>
                          {category.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {category.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {category.parent ? (
                        <span className="badge badge-info">{category.parent.name}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">Aucune</span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-secondary">{category.sort_order || 0}</span>
                    </td>
                    <td>
                      <span className={`badge ${category.is_active ? 'badge-success' : 'badge-warning'}`}>
                        {category.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Bouton Détails - Bleu/Info */}
                        <button
                          onClick={() => handleViewDetails(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{ backgroundColor: '#3b82f6' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          title="Voir les détails"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Détails
                        </button>

                        {/* Bouton Modifier - Jaune */}
                        <button
                          onClick={() => handleEdit(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{ backgroundColor: '#eab308' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#ca8a04'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#eab308'}
                          title="Modifier la catégorie"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Modifier
                        </button>

                        {/* Bouton Activer/Désactiver - Orange/Vert */}
                        <button
                          onClick={() => toggleStatus(category.id)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{
                            backgroundColor: category.is_active ? '#f97316' : '#22c55e'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = category.is_active ? '#ea580c' : '#16a34a';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = category.is_active ? '#f97316' : '#22c55e';
                          }}
                          title={category.is_active ? 'Désactiver la catégorie' : 'Activer la catégorie'}
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {category.is_active ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            )}
                          </svg>
                          {category.is_active ? 'Désactiver' : 'Activer'}
                        </button>

                        {/* Bouton Supprimer - Rouge */}
                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{ backgroundColor: '#ef4444' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          title="Supprimer la catégorie"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-4 py-3">
            <div className="text-sm text-gray-700">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredCategories.length)} sur {filteredCategories.length} catégorie{filteredCategories.length > 1 ? 's' : ''}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <span className="flex items-center px-4 text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'ajout/modification */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { resetForm(); setShowAddModal(false); }}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingCategory ? 'Modifier la Catégorie' : 'Ajouter une Catégorie'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => { resetForm(); setShowAddModal(false); }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="space-y-4">
                <div className="modal-form-group">
                  <label className="modal-form-label">
                    Nom de la catégorie *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="modal-form-input"
                    required
                    placeholder="Ex: Légumes"
                  />
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="modal-form-textarea"
                    rows="3"
                    placeholder="Description de la catégorie"
                  />
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">
                    Icône (emoji)
                  </label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="modal-form-input"
                    placeholder="Ex: 🥕"
                    maxLength="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Facultatif : Utilisez un emoji pour représenter la catégorie
                  </p>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">
                    Catégorie parente
                  </label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="modal-form-select"
                  >
                    <option value="">Aucune (catégorie principale)</option>
                    {getParentCategories().map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Facultatif : Sélectionnez une catégorie parente pour créer une sous-catégorie
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Ordre de tri
                    </label>
                    <input
                      type="number"
                      name="sort_order"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                      className="modal-form-input"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ordre d'affichage (0 = premier, 1 = deuxième, etc.)
                    </p>
                  </div>

                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Statut
                    </label>
                    <select
                      name="is_active"
                      value={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                      className="modal-form-select"
                    >
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { resetForm(); setShowAddModal(false); }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedCategory && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Détails de la Catégorie</h2>
              <button
                className="modal-close"
                onClick={() => setShowDetailsModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Nom</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedCategory.icon && <span className="mr-2">{selectedCategory.icon}</span>}
                    {selectedCategory.name}
                  </p>
                </div>

                {selectedCategory.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-700">{selectedCategory.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Catégorie parente</label>
                    <p className="text-gray-700">{selectedCategory.parent?.name || 'Aucune'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Ordre de tri</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedCategory.sort_order || 0}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Statut</label>
                  <p>
                    <span className={`badge ${selectedCategory.is_active ? 'badge-success' : 'badge-warning'}`}>
                      {selectedCategory.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </p>
                </div>

                {selectedCategory.created_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date de création</label>
                    <p className="text-gray-700">
                      {new Date(selectedCategory.created_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedCategory && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirmer la suppression</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer la catégorie <strong>"{selectedCategory.name}"</strong> ?
              </p>
              <p className="text-sm text-red-600 mt-2">
                Cette action est irréversible.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
                style={{ backgroundColor: '#ef4444' }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
